import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Button, Input, Select, SelectOption, Tabs } from '@freelancelabs/components';
import { MagnifyingGlass } from '@freelancelabs/icons';

import { PortalModulesDocument } from '../../graphql/__generated__/types';
import AccountsList from './AccountsList';
import GroupedAccounts from './GroupedAccounts';

/** Long enough not to query on every keystroke, short enough not to feel laggy. */
const SEARCH_DEBOUNCE_MS = 300;

/** Open, closed, or both. `ALL` is the absence of the argument, and travels as null. */
type PortalFilter = 'ALL' | 'OPEN' | 'CLOSED';

/**
 * La page « Comptes » du back-office, groupée par société mère (PCG-333).
 *
 * **Deux vues, une bascule.** La vue GROUPÉE (par défaut) rend les sociétés mères en
 * accordéon, chacune dépliant ses comptes, plus une section à plat pour les comptes
 * indépendants. La vue À PLAT rend tous les comptes en une seule table, avec la
 * colonne « Société mère ». La bascule est un `Tabs` segmenté, et la barre de recherche
 * et le filtre portail lui sont communs : les deux vues répondent à la même question.
 *
 * **La recherche traverse.** En vue groupée elle filtre les sociétés par nom côté
 * client (index complet, filtre légal) et les comptes indépendants côté serveur ; en
 * vue à plat elle va à `accounts(search:)`. Le filtre portail est câblé sur
 * `portalEnabled` des requêtes `accounts`, et masque côté client une société sans
 * compte de l'état demandé.
 *
 * Les compteurs, le SIREN nul, la société non résolue : tout est tenu par
 * `AccountsTable` et `AccountSocietyPanel`, une seule source pour chaque règle.
 */
const AccountsPage = () => {
    const [view, setView] = useState<0 | 1>(0);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [portal, setPortal] = useState<PortalFilter>('ALL');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    /**
     * Le dénominateur de la colonne « Modules », et le seul endroit où il se lit.
     * `portalModules` nomme le menu entier du portail et grandit avec lui ; « sur 6 »
     * écrit ici serait un nombre faux le jour d'un septième module.
     */
    const { data: modulesData } = useQuery(PortalModulesDocument);
    const moduleTotal = modulesData?.portalModules?.length ?? null;

    const portalEnabled = portal === 'ALL' ? null : portal === 'OPEN';

    const portalOptions: SelectOption<PortalFilter>[] = [
        { label: c('Accounts filter').t`Open and closed`, value: 'ALL' },
        { label: c('Accounts filter').t`Open to the portal`, value: 'OPEN' },
        { label: c('Accounts filter').t`Closed to the portal`, value: 'CLOSED' },
    ];

    const filtered = Boolean(search) || portal !== 'ALL';

    const clearFilters = () => {
        setSearch('');
        setDebouncedSearch('');
        setPortal('ALL');
    };

    const toolbar = (
        <div className="flex w-full flex-wrap items-center gap-3">
            <Input
                name="account-search"
                value={search}
                onValue={setSearch}
                clearable
                prefix={<MagnifyingGlass size={16} />}
                placeholder={c('Accounts filter').t`Account name or SIREN`}
                aria-label={c('Accounts filter').t`Search an account`}
                className="min-w-0 flex-1 basis-[26rem]"
            />
            <Select
                options={portalOptions}
                value={portalOptions.find((option) => option.value === portal)}
                onChange={(option) => setPortal(option.value)}
                aria-label={c('Accounts filter').t`Filter on the portal access`}
                className="max-w-xs"
            />
            {filtered && (
                <Button category="weak" size="small" onClick={clearFilters}>
                    {c('Accounts').t`Clear filters`}
                </Button>
            )}
        </div>
    );

    const tabs = useMemo(
        () => [
            {
                label: c('Accounts view').t`Group by company`,
                content: (
                    <GroupedAccounts
                        debouncedSearch={debouncedSearch}
                        portalEnabled={portalEnabled}
                        moduleTotal={moduleTotal}
                    />
                ),
            },
            {
                label: c('Accounts view').t`Flat view`,
                content: (
                    <AccountsList
                        debouncedSearch={debouncedSearch}
                        portalEnabled={portalEnabled}
                        moduleTotal={moduleTotal}
                        showCompany
                        label={c('Accounts').t`Accounts list`}
                        emptyTitle={c('Accounts').t`No account in your portfolio.`}
                        emptyDescription={c('Accounts')
                            .t`Nothing was refused: the accounts you may act on are simply none for now. An administrator assigns them, account by account, unless your role sees them all.`}
                    />
                ),
            },
        ],
        [debouncedSearch, portalEnabled, moduleTotal]
    );

    return (
        <section className="flex flex-col gap-5">
            <header>
                <h1 className="text-xl font-semibold text-text-900">{c('Accounts').t`Accounts`}</h1>
                <p className="mt-1 max-w-2xl text-xs text-text-600">
                    {c('Accounts')
                        .t`The client accounts of your portfolio, grouped by the parent company they belong to, and how each one is configured for the portal.`}
                </p>
            </header>

            <Tabs
                variant="segmented"
                tabs={tabs}
                selectedIndex={view}
                onChange={(index) => setView(index as 0 | 1)}
                toolbar={toolbar}
                toolbarLeading
            />
        </section>
    );
};

export default AccountsPage;
